import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import Navbar from '../components/Navbar';
import { Calendar, Clock } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AvailabilityEditPage = () => {
  const navigate = useNavigate();
  const { instructorId, date } = useParams();
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [fetchingData, setFetchingData] = useState(true);
  const [formData, setFormData] = useState({
    instructor_id: '',
    dates: [date],
    start_time: '09:00',
    end_time: '21:00',
    duration_minutes: 40,
    pause_start: '',
    pause_end: ''
  });

  useEffect(() => {
    fetchAvailability();
  }, [instructorId, date]);

  const fetchAvailability = async () => {
    setFetchingData(true);
    try {
      const response = await axios.get(
        `${API}/admin/availability/${instructorId}/${date}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const availability = response.data;
      
      // Calculate the original time parameters from existing slots
      if (availability.time_slots && availability.time_slots.length > 0) {
        const firstSlot = availability.time_slots[0];
        const lastSlot = availability.time_slots[availability.time_slots.length - 1];
        
        setFormData({
          instructor_id: availability.instructor_id,
          dates: [availability.date],
          start_time: firstSlot.start_time,
          end_time: lastSlot.end_time,
          duration_minutes: firstSlot.duration_minutes || 40,
          pause_start: '',
          pause_end: ''
        });
      }
    } catch (error) {
      console.error('Error fetching availability:', error);
      toast.error('Failed to load availability');
      navigate('/admin');
    } finally {
      setFetchingData(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    setLoading(true);
    try {
      await axios.put(
        `${API}/admin/availability/${instructorId}/${date}`,
        {
          instructor_id: instructorId,
          dates: [date],
          start_time: formData.start_time,
          end_time: formData.end_time,
          duration_minutes: parseInt(formData.duration_minutes),
          pause_start: formData.pause_start || null,
          pause_end: formData.pause_end || null
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success('Availability updated successfully!');
      navigate('/admin');
    } catch (error) {
      console.error('Error updating availability:', error);
      toast.error(error.response?.data?.detail || 'Failed to update availability');
    } finally {
      setLoading(false);
    }
  };

  if (fetchingData) {
    return (
      <div>
        <Navbar />
        <div className="booking-page" style={{ paddingTop: '100px', textAlign: 'center' }}>
          <p>Loading availability...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Navbar />
      <div className="booking-page" style={{ paddingTop: '100px' }}>
        <div style={{ maxWidth: '800px', margin: '0 auto', padding: '2rem' }}>
          <h1 className="booking-title" style={{ marginBottom: '0.5rem' }}>
            Edit Availability
          </h1>
          <p style={{ color: '#666', marginBottom: '2rem' }}>
            Edit availability for {date}
          </p>

          <form onSubmit={handleSubmit}>
            {/* Instructor (read-only) */}
            <div className="form-group">
              <label className="form-label">
                <Calendar size={18} style={{ display: 'inline', marginRight: '0.5rem' }} />
                Instructor
              </label>
              <input
                type="text"
                className="form-input"
                value={instructorId === 'julie-christiansen' ? 'Julie Christiansen' : 
                       instructorId === 'oscar' ? 'Óskar Erik Kristjánsson' : instructorId}
                disabled
                style={{ background: '#f5f5f5', cursor: 'not-allowed' }}
              />
            </div>

            {/* Date (read-only) */}
            <div className="form-group">
              <label className="form-label">Date</label>
              <input
                type="text"
                className="form-input"
                value={new Date(date + 'T00:00:00').toLocaleDateString('da-DK', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
                disabled
                style={{ background: '#f5f5f5', cursor: 'not-allowed' }}
              />
              <p style={{ fontSize: '0.875rem', color: '#666', marginTop: '0.5rem' }}>
                To change the date, delete this availability and create a new one
              </p>
            </div>

            {/* Time Range */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <div className="form-group">
                <label className="form-label">
                  <Clock size={18} style={{ display: 'inline', marginRight: '0.5rem' }} />
                  Start Time
                </label>
                <input
                  type="time"
                  className="form-input"
                  value={formData.start_time}
                  onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                  step="300"
                  required
                />
                <p style={{ fontSize: '0.875rem', color: '#666', marginTop: '0.25rem' }}>
                  5-minute increments
                </p>
              </div>

              <div className="form-group">
                <label className="form-label">
                  <Clock size={18} style={{ display: 'inline', marginRight: '0.5rem' }} />
                  End Time
                </label>
                <input
                  type="time"
                  className="form-input"
                  value={formData.end_time}
                  onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                  step="300"
                  required
                />
                <p style={{ fontSize: '0.875rem', color: '#666', marginTop: '0.25rem' }}>
                  5-minute increments
                </p>
              </div>
            </div>

            {/* Duration */}
            <div className="form-group">
              <label className="form-label">Lesson Duration (minutes)</label>
              <select
                className="form-input"
                value={formData.duration_minutes}
                onChange={(e) => setFormData({ ...formData, duration_minutes: e.target.value })}
                required
              >
                <option value="30">30 minutes</option>
                <option value="40">40 minutes</option>
                <option value="45">45 minutes</option>
                <option value="60">60 minutes</option>
                <option value="90">90 minutes</option>
                <option value="120">120 minutes</option>
              </select>
              <p style={{ fontSize: '0.875rem', color: '#666', marginTop: '0.5rem' }}>
                Time slots will be regenerated based on this duration
              </p>
            </div>

            {/* Pause (optional) */}
            <div style={{ 
              background: '#f5f5f5', 
              padding: '1rem', 
              borderRadius: '8px',
              marginBottom: '1rem'
            }}>
              <h3 style={{ fontSize: '1rem', marginBottom: '1rem' }}>
                Lunch Break (Optional)
              </h3>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">Pause Start</label>
                  <input
                    type="time"
                    className="form-input"
                    value={formData.pause_start}
                    onChange={(e) => setFormData({ ...formData, pause_start: e.target.value })}
                    step="300"
                  />
                </div>

                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">Pause End</label>
                  <input
                    type="time"
                    className="form-input"
                    value={formData.pause_end}
                    onChange={(e) => setFormData({ ...formData, pause_end: e.target.value })}
                    step="300"
                  />
                </div>
              </div>
              <p style={{ fontSize: '0.875rem', color: '#666', marginTop: '0.5rem' }}>
                Leave empty if no break needed. No slots will be created during this time.
              </p>
            </div>

            {/* Warning */}
            <div style={{
              background: '#fff3e0',
              border: '1px solid #ff9800',
              padding: '1rem',
              borderRadius: '8px',
              marginTop: '1.5rem',
              marginBottom: '1.5rem'
            }}>
              <h3 style={{ fontSize: '1rem', marginBottom: '0.5rem', color: '#f57c00' }}>
                ⚠️ Warning
              </h3>
              <p style={{ color: '#666', fontSize: '0.9rem' }}>
                Updating this availability will regenerate all time slots for this date. 
                Existing bookings will be preserved, but any changes may affect unbooked slots.
              </p>
            </div>

            {/* Buttons */}
            <div style={{ display: 'flex', gap: '1rem', marginTop: '2rem' }}>
              <button
                type="button"
                onClick={() => navigate('/admin')}
                style={{
                  flex: 1,
                  padding: '0.75rem',
                  background: '#e5e5e5',
                  color: '#333',
                  border: 'none',
                  borderRadius: '5px',
                  cursor: 'pointer',
                  fontSize: '1rem',
                  fontWeight: 500
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="cta-button"
                disabled={loading}
                style={{ flex: 1, justifyContent: 'center' }}
              >
                {loading ? 'Updating...' : 'Update Availability'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AvailabilityEditPage;
